import { Component, createResource, createSignal, Show, useContext } from 'solid-js';
import { Modal } from '../../ui-components/modal/modal';
import { Button } from '../../ui-components/button/button';
import { Text } from '../../ui-components/text/text';
import { Heading } from '../../ui-components/heading/heading';
import { Checkbox } from '../../ui-components/inputs/checkbox/checkbox';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { SiteDomain } from '../../types/app-state';
import { atosGlobalCountrySlugToCountryCode } from '../../tools/countries-data';

export type UserType = 'hcp' | 'patient';

export type PrivacyPolicyModalProps = {
    visible: boolean;
    onClose: () => void;
    title?: string;
    /**
     * User's ISO country code (e.g., "SE", "KR").
     * Used to determine which site's privacy policy to display.
     * If the country doesn't have a dedicated privacy policy, falls back to international.
     * If not provided, uses the current site's privacy policy.
     */
    userCountryCode?: string;
    /**
     * User type: 'hcp' (Healthcare Professional) or 'patient'.
     * Used to determine which terms to display (HCP-specific or Patient-specific).
     * If not provided, falls back to HCP terms (backwards compatible).
     */
    userType?: UserType;
    /**
     * Description text shown at the top of the modal (for accept mode).
     */
    description?: string;
    /**
     * Callback when user accepts the policy. If provided, shows accept mode
     * with consent checkbox and Accept button instead of just Close button.
     * Receives marketingConsent boolean.
     */
    onAccept?: (marketingConsent: boolean) => void;
    /**
     * Whether the accept button is in loading state.
     */
    loading?: boolean;
    /**
     * Whether to show marketing consent checkbox (optional, default false).
     */
    showMarketingConsent?: boolean;
    /**
     * Type of content to display: 'privacy' (default) or 'marketing'.
     * Determines which content is fetched from terms and conditions.
     */
    contentType?: 'privacy' | 'marketing';
};

/**
 * Find the international site from siteDomains.
 */
const getInternationalSiteId = (siteDomains: SiteDomain[]): string | null => {
    const internationalSite = siteDomains.find(site => site.siteSlug === 'international');
    return internationalSite?.siteId || null;
};

/**
 * Find the siteId for a given ISO country code using the siteDomains from context.
 * Matches via country code → site slug mapping instead of country name.
 * Falls back to null if no match found.
 */
const getSiteIdForCountryCode = (siteDomains: SiteDomain[], countryCode: string): string | null => {
    const matchingSite = siteDomains.find(site =>
        atosGlobalCountrySlugToCountryCode[site.siteSlug] === countryCode
    );

    if (matchingSite) {
        return matchingSite.siteId;
    }

    return null;
};

/**
 * Result type for extracted content including metadata
 */
type ExtractedContent = {
    content: string | null;
    updatedAt?: string;
    sourceSiteId?: string;
};

/**
 * Extracts content from terms data based on userType and contentType.
 * Falls back to flat structure if hcp/patient structure not available.
 * Also returns updatedAt if available.
 */
const extractContent = (
    terms: any,
    userType: UserType = 'hcp',
    contentType: 'privacy' | 'marketing'
): ExtractedContent => {
    // Try new structure first (hcp/patient)
    const userTypeTerms = terms[userType];
    if (userTypeTerms) {
        const termData = contentType === 'marketing'
            ? userTypeTerms.marketingConsent
            : userTypeTerms.privacyPolicy;
        if (termData?.content) {
            return {
                content: termData.content,
                updatedAt: termData.updatedAt
            };
        }
    }

    // Fall back to flat structure (backwards compatibility)
    const termData = contentType === 'marketing'
        ? terms.marketingConsent
        : terms.privacyPolicy;

    return {
        content: termData?.content || null,
        updatedAt: termData?.updatedAt
    };
};

/**
 * Rewrites relative links in HTML content to use the correct site domain.
 * When privacy policy content is fetched from a different site (e.g., German policy
 * shown on Swedish site), relative links like /datenschutz/ would resolve against
 * the current site's domain. This function prefixes them with the source site's domain.
 */
const rewriteRelativeLinks = (
    html: string,
    sourceSiteId: string | undefined,
    currentSiteId: string,
    siteDomains: SiteDomain[],
    target: string
): string => {
    if (!sourceSiteId || sourceSiteId === currentSiteId) {
        return html;
    }

    const sourceSite = siteDomains.find(site => site.siteId === sourceSiteId);
    const domain = sourceSite?.domains?.[target] || sourceSite?.domains?.['live'];
    if (!domain) {
        return html;
    }

    const baseUrl = `https://${domain}`;

    // Rewrite href="/..." to href="https://domain/..."
    return html.replace(/href="\/(?!\/)/g, `href="${baseUrl}/`);
};

/**
 * Fetches terms and conditions content (HTML) based on user's country and type.
 * Uses the /api-client/terms-and-conditions server-side endpoint which has access
 * to the SECURE_FETCH_TOKEN needed to fetch terms-and-conditions.
 * Falls back to international site if country-specific content not found.
 *
 * @param contentType - 'privacy' for privacy policy content, 'marketing' for marketing consent content
 * @param userType - 'hcp' or 'patient' to get type-specific content
 */
const fetchTermsContent = async (
    siteInfo: any,
    siteDomains: SiteDomain[],
    contentType: 'privacy' | 'marketing' = 'privacy',
    userType: UserType = 'hcp',
    userCountryCode?: string
): Promise<ExtractedContent | null> => {
    // Get the international site for fallback
    const internationalSiteId = getInternationalSiteId(siteDomains);

    // Determine which site's content to fetch
    let targetSiteId: string;

    if (userCountryCode) {
        // If current site matches the selected country, use it (respects language variants like canada-fr)
        const currentSiteCountryCode = siteInfo?.siteSlug ? atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug] : undefined;
        if (currentSiteCountryCode === userCountryCode) {
            targetSiteId = siteInfo?.siteId;
        } else {
            // Look up siteId from siteDomains based on country code → site slug mapping
            const foundSiteId = getSiteIdForCountryCode(siteDomains, userCountryCode);
            // Fall back to international site if country not found
            targetSiteId = foundSiteId || internationalSiteId || siteInfo?.siteId;
        }
    } else {
        // No country specified - use current site
        targetSiteId = siteInfo?.siteId;
    }

    try {
        const response = await fetch('/api-client/terms-and-conditions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ siteId: targetSiteId }),
        });

        const data = await response.json();

        if (data.success && data.termsAndConditions) {
            const extracted = extractContent(data.termsAndConditions, userType, contentType);
            if (extracted.content) {
                return { ...extracted, sourceSiteId: targetSiteId };
            }
        }

        // If not found and we tried a country-specific site, fall back to international
        if (targetSiteId !== internationalSiteId && internationalSiteId) {
            const fallbackResponse = await fetch('/api-client/terms-and-conditions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ siteId: internationalSiteId }),
            });

            const fallbackData = await fallbackResponse.json();
            if (fallbackData.success && fallbackData.termsAndConditions) {
                const fallbackExtracted = extractContent(fallbackData.termsAndConditions, userType, contentType);
                if (fallbackExtracted.content) {
                    return { ...fallbackExtracted, sourceSiteId: internationalSiteId };
                }
            }
        }

        return null;
    } catch (error) {
        console.error('[TermsContent] Error fetching:', error);
        return null;
    }
};

/**
 * Privacy Policy Modal
 *
 * Displays privacy policy content in a modal. The content is fetched
 * based on the user's selected country, falling back to the international
 * site's policy if the selected country doesn't have a dedicated privacy policy.
 *
 * Supports two modes:
 * 1. View-only mode (default): Just displays policy with Close button
 * 2. Accept mode (when onAccept provided): Shows consent checkbox and Accept button
 */
export const PrivacyPolicyModal: Component<PrivacyPolicyModalProps> = (props) => {
    // Use the unfiltered `allSiteDomains` so atos-care can still resolve atos-medical country
    // sites when looking up per-country privacy/marketing terms.
    const { localize, siteInfo, allSiteDomains: siteDomains } = useContext(AppContext);

    // State for accept mode checkboxes
    const [policyAccepted, setPolicyAccepted] = createSignal(false);
    const [marketingConsent, setMarketingConsent] = createSignal(false);

    // Determine if we're in accept mode
    const isAcceptMode = () => !!props.onAccept;

    // Fetch content based on user's country, user type, and content type
    // Re-fetch when visible changes, userCountryCode changes, userType changes, or contentType changes
    const [policyContent] = createResource(
        () => ({
            visible: props.visible,
            userCountryCode: props.userCountryCode,
            userType: props.userType || 'hcp',
            contentType: props.contentType || 'privacy'
        }),
        async ({ visible, userCountryCode, userType, contentType }) => {
            if (!visible) return null;
            // Reset checkboxes when modal opens
            setPolicyAccepted(false);
            setMarketingConsent(false);
            const content = await fetchTermsContent(
                siteInfo,
                siteDomains,
                contentType,
                userType,
                userCountryCode
            );
            return content;
        }
    );

    const handleAccept = () => {
        if (props.onAccept && policyAccepted()) {
            props.onAccept(marketingConsent());
        }
    };

    return (
        <ErrorCatcher componentName="PrivacyPolicyModal">
            {/* The shell caps the panel against the viewport and this modal keeps its own
                scroller, so the body is a flex column and the wrapper flexes to fill it.
                It used to cap itself at max-h-[70vh]: viewport-relative, so once the shell
                gained its own cap the wrapper could end up taller than the panel and the
                forced `!overflow-hidden` clipped the overflow away on short viewports. */}
            <Modal visible={props.visible} onClose={props.onClose} size="full" darkenBackground={true} bodyClassName="!pb-0 !overflow-hidden flex flex-col">
                <div class="flex min-h-0 flex-1 flex-col">
                    {/* Header - only show for accept mode (login flow) where we need context */}
                    <Show when={isAcceptMode() || props.description}>
                        <div class="flex-shrink-0 px-8 pb-4">
                            <Show when={isAcceptMode()}>
                                <Heading tag="h2">
                                    {props.title || localize('privacy-policy', 'Privacy Policy')}
                                </Heading>
                            </Show>
                            <Show when={props.description}>
                                <Text fontSize="small" class="mt-2 text-grey-500">
                                    {props.description}
                                </Text>
                            </Show>
                        </div>
                    </Show>

                    {/* Scrollable content area - scrollbar only when needed */}
                    <div
                        class="flex-1 overflow-y-auto px-8 min-h-0 mr-2
                            [&::-webkit-scrollbar]:w-[6px]
                            [&::-webkit-scrollbar-track]:bg-grey-100
                            [&::-webkit-scrollbar-track]:rounded-full
                            [&::-webkit-scrollbar-thumb]:bg-grey-400
                            [&::-webkit-scrollbar-thumb]:rounded-full
                            [&::-webkit-scrollbar-thumb:hover]:bg-grey-500"
                        style={{ 'scrollbar-width': 'thin', 'scrollbar-color': '#918787 #EBE9E9' }}
                    >
                        <Show
                            when={!policyContent.loading && policyContent()?.content}
                            fallback={
                                <div class="flex flex-col items-center justify-center py-16 gap-4">
                                    <div class="w-8 h-8 border-4 border-grey-200 border-t-grey-500 rounded-full animate-spin" />
                                    <Text fontSize="small" color="grey500">
                                        {localize('loading-policy', 'Loading policy...')}
                                    </Text>
                                </div>
                            }
                        >
                            {/* Render HTML content from ACF WYSIWYG field - centered container with left-aligned text */}
                            <div class="max-w-3xl mx-auto text-left mb-6">
                                <div
                                    class="text-small leading-relaxed pb-4 [&_h1]:text-normal [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-normal [&_h2]:font-bold [&_h2]:mb-3 [&_h3]:text-small [&_h3]:font-bold [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline [&_a:hover]:text-grey-500"
                                    innerHTML={rewriteRelativeLinks(
                                        policyContent()?.content || '',
                                        policyContent()?.sourceSiteId,
                                        siteInfo?.siteId,
                                        siteDomains,
                                        siteInfo?.target || 'live'
                                    )}
                                />
                            </div>
                        </Show>
                    </div>

                    {/* Footer - fixed at bottom */}
                    {/* Accept mode: show checkboxes and Accept/Cancel buttons */}
                    <Show when={isAcceptMode()}>
                        <div class="flex-shrink-0 border-t border-grey-200 pt-4 pb-6 px-8 bg-white">
                            <div class="flex flex-col gap-3 mb-4">
                                <Checkbox
                                    id="policy-consent-checkbox"
                                    isChecked={policyAccepted()}
                                    whenClicked={() => setPolicyAccepted(!policyAccepted())}
                                    value="policy-consent"
                                    name={localize('agree-to-terms', 'I agree to the terms and conditions')}
                                />
                                {/* Dead code - Nothing passes the prop showMarketingConsent  */}
                                <Show when={props.showMarketingConsent}>
                                    <Checkbox
                                        id="marketing-consent-checkbox"
                                        isChecked={marketingConsent()}
                                        whenClicked={() => setMarketingConsent(!marketingConsent())}
                                        value="marketing-consent"
                                        name={localize('marketing-consent', 'I would like to receive marketing communications')}
                                    />
                                </Show>
                            </div>
                            <div class="flex justify-end gap-4">
                                <Button
                                    variant="secondary"
                                    label={localize('cancel', 'Cancel')}
                                    onClick={props.onClose}
                                    disabled={props.loading}
                                    noBlockSpacing
                                />
                                <Button
                                    variant="primary"
                                    label={props.loading
                                        ? localize('loading', 'Loading...')
                                        : localize('accept-and-continue', 'Accept and Continue')
                                    }
                                    onClick={handleAccept}
                                    disabled={!policyAccepted() || props.loading}
                                    noBlockSpacing
                                />
                            </div>
                        </div>
                    </Show>

                    {/* View-only mode: just show Close button */}
                    <Show when={!isAcceptMode()}>
                        <div class="flex-shrink-0 flex justify-between items-center border-t border-grey-200 pt-4 pb-6 px-8 bg-white">
                            <Show when={policyContent()?.updatedAt} fallback={<div />}>
                                <span class="text-smaller text-grey-500">
                                    {localize('last-updated', 'Last updated')}: {policyContent()?.updatedAt}
                                </span>
                            </Show>
                            <Button
                                variant="secondary"
                                label={localize('close', 'Close')}
                                onClick={props.onClose}
                            />
                        </div>
                    </Show>
                </div>
            </Modal>
        </ErrorCatcher>
    );
};
